import { createFileRoute, Link } from '@tanstack/react-router'
import {
  ArrowRight,
  FileText,
  FolderKanban,
  Lightbulb,
  Notebook,
  Users,
  type LucideIcon,
} from 'lucide-react'
import { isFeatureEnabled } from '@/lib/feature-flags'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { PageBody, PageContainer, Section } from '@/components/ui/page-container'
import { PageHeader, SectionHeader } from '@/components/ui/page-header'
import { GatedFeaturePlaceholder } from '@/components/GatedFeaturePlaceholder'
import ErrorFallback from '@/components/ErrorFallback'

export const Route = createFileRoute('/_authenticated/projects/$projectSlug/')({
  component: ProjectHomePage,
  errorComponent: ErrorFallback,
})

export function ProjectHomePage() {
  const { projectSlug } = Route.useParams()

  if (!isFeatureEnabled('collab')) {
    return (
      <GatedFeaturePlaceholder
        title={`Project: ${projectSlug}`}
        description="The collaboration shell renders here when the feature flag is enabled."
        icon={FolderKanban}
        flagName="VITE_FF_COLLAB"
      />
    )
  }

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Project"
        title={projectSlug}
        description="A scoped home for everything a fill needs — documents, prompts, context, and the teammates working with you."
      />
      <PageBody>
        <Section aria-label="Project sections">
          <SectionHeader
            title="Jump in"
            description="Pick a surface. Each one is a shell today; full content lands as it ships."
          />
          <div className="grid gap-3 md:grid-cols-2">
            <Link
              to="/projects/$projectSlug/prompts"
              params={{ projectSlug }}
              className="group rounded-lg outline-none focus-visible:ring-[3px] focus-visible:ring-ring/40"
            >
              <SectionTile
                label="Prompts"
                description="Reusable instructions a teammate or the agent can run."
                icon={Lightbulb}
              />
            </Link>
            <Link
              to="/projects/$projectSlug/context"
              params={{ projectSlug }}
              className="group rounded-lg outline-none focus-visible:ring-[3px] focus-visible:ring-ring/40"
            >
              <SectionTile
                label="Context packs"
                description="Shared facts/snippets that get snapshotted into each fill."
                icon={Notebook}
              />
            </Link>
            <Link
              to="/projects/$projectSlug/members"
              params={{ projectSlug }}
              className="group rounded-lg outline-none focus-visible:ring-[3px] focus-visible:ring-ring/40"
            >
              <SectionTile
                label="Members"
                description="Invite teammates and manage roles."
                icon={Users}
              />
            </Link>
            <Link
              to="/uploads"
              className="group rounded-lg outline-none focus-visible:ring-[3px] focus-visible:ring-ring/40"
            >
              <SectionTile
                label="Uploads"
                description="Cross-project upload tray — pick up where you left off."
                icon={FileText}
              />
            </Link>
          </div>
        </Section>

        <Section aria-label="Project overview">
          <SectionHeader
            title="Overview"
            description="Activity rollups for this project — recent docs, pending invites, recent fills."
          />
          <Card>
            <CardContent>
              <EmptyState
                variant="bare"
                icon={FolderKanban}
                title="Activity feed lands here"
                description="Once the projects API ships, this surface will show the project's recent documents, pending invites, and prompt usage."
              />
            </CardContent>
          </Card>
        </Section>
      </PageBody>
    </PageContainer>
  )
}

interface SectionTileProps {
  label: string
  description: string
  icon: LucideIcon
}

function SectionTile({ label, description, icon: Icon }: SectionTileProps) {
  return (
    <Card interactive className="aurora-card-hover h-full">
      <CardHeader>
        <div className="flex items-start gap-3">
          <span
            aria-hidden
            className="mt-0.5 inline-flex size-9 shrink-0 items-center justify-center rounded-md bg-primary-soft text-primary-soft-foreground"
          >
            <Icon className="size-4" />
          </span>
          <div className="min-w-0 space-y-1">
            <CardTitle>{label}</CardTitle>
            <CardDescription>{description}</CardDescription>
          </div>
          <ArrowRight className="ml-auto mt-1.5 size-4 shrink-0 text-muted-foreground/70 transition-transform group-hover:translate-x-0.5 group-hover:text-foreground" />
        </div>
      </CardHeader>
    </Card>
  )
}
